import React from 'react';

import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import { useTranslation } from '@/i18n';
import type { ShiftDayCellViewModel, AssignedScheduleView } from '@/hooks/shift/useShiftWeeklyDistribution';


interface ShiftDayCardProps {
  day: ShiftDayCellViewModel;
  onClick: (daySequence: number) => void;
}

const RestDayIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke={colors.feedback.error300} strokeWidth="1.5" />
    <line x1="6" y1="18" x2="18" y2="6" stroke={colors.feedback.error300} strokeWidth="1.5" />
  </svg>
);

const formatNetHours = (hours: number): string => (
  Number.isInteger(hours) ? String(hours) : hours.toFixed(1)
);

const ScheduleBlock: React.FC<{ schedule: AssignedScheduleView; accentColor: string }> = ({
  schedule,
  accentColor,
}) => {
  const { t: translate } = useTranslation('schedule');

  return (
    // Tarjeta blanca con barra de acento a la izquierda y sombra suave (según diseño),
    // en lugar de un bloque plano separado por una línea inferior.
    <div
      className="flex gap-2 rounded-md overflow-hidden mb-2"
      style={{
        backgroundColor: colors.grays.neutralFF,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
      }}
    >
      <div style={{ width: 3, backgroundColor: accentColor, flexShrink: 0 }} />
      <div className="flex flex-col py-2 pr-2">
        <Text variant="small-medium" color={colors.grays.neutral33}>
          {schedule.name}
        </Text>
        <Text variant="small-regular" color={colors.feedback.alert300}>
          {schedule.startTime} - {schedule.endTime}
        </Text>
        <Text variant="small-regular" color={colors.important.main}>
          {translate('shift.create.fields.workHours', { hours: formatNetHours(schedule.netHours) })}
        </Text>
        <Text variant="small-regular" color={colors.important.main}>
          {translate('shift.create.fields.breakMinutesShort', { minutes: schedule.breakMinutes })}
        </Text>
      </div>
    </div>
  );
};

/**
 * Nombre del día (LUN, MAR…) centrado ARRIBA de la tarjeta, fuera de ella —
 * según diseño. No se renderiza en ciclos que no se anclan a días de la semana
 * (Distribuida en ciclos), donde solo aplica el número de día (D1, D2…).
 */
const DayName: React.FC<{ label: string | null }> = ({ label }) => (
  label
    ? (
      <Text variant="small-medium" color={colors.grays.neutral33} className="text-center">
        {label}
      </Text>
    )
    : null
);

/** Número de día (D1, D2…) siempre en la esquina superior derecha de la tarjeta. */
const DayNumber: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex justify-end w-full">
    <Text variant="small-medium" color={colors.primary.dash}>{label}</Text>
  </div>
);

export const ShiftDayCard: React.FC<ShiftDayCardProps> = ({ day, onClick }) => {
  const { t: translate } = useTranslation('schedule');

  const dayLabel = day.dayLabelKey ? translate(`shift.create.fields.days.${day.dayLabelKey}`) : null;
  const hasAssignment = day.schedule1 !== null;

  if (day.isOffPattern) {
    return (
      <div className="flex flex-col gap-2">
        <DayName label={dayLabel} />
        <div
          className="rounded-lg p-4 flex flex-col"
          style={{ backgroundColor: colors.grays.neutralFA, minHeight: 140 }}
          data-testid={`shift-day-card-${day.daySequence}`}
        >
          <DayNumber label={day.dayNumberLabel} />
          {/* El ícono y la etiqueta se centran en el espacio restante, dejando el
              número de día alineado arriba igual que en las tarjetas asignables. */}
          <div className="flex-1 flex flex-col items-center justify-center gap-2">
            <RestDayIcon />
            <Text variant="small-regular" color={colors.grays.neutral66}>
              {translate('shift.create.fields.restDay')}
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <DayName label={dayLabel} />
      <button
        type="button"
        onClick={() => onClick(day.daySequence)}
        className="rounded-lg p-4 flex flex-col text-left w-full"
        style={{ backgroundColor: colors.grays.neutralFA, minHeight: 140, cursor: 'pointer' }}
        data-testid={`shift-day-card-${day.daySequence}`}
      >
        <DayNumber label={day.dayNumberLabel} />

        {hasAssignment ? (
          <div className="mt-2">
            {day.schedule1 && (
              <ScheduleBlock schedule={day.schedule1} accentColor={colors.important.main} />
            )}
            {day.schedule2 && (
              <ScheduleBlock schedule={day.schedule2} accentColor={colors.feedback.success300} />
            )}
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-1 mt-2">
            <Text variant="small-medium" color={colors.important.main}>+</Text>
            <Text variant="small-regular" color={colors.important.main}>
              {translate('shift.create.fields.assignSchedule')}
            </Text>
          </div>
        )}
      </button>
    </div>
  );
};

export default ShiftDayCard;
